import { DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { BOARD_STAGES, formatDay, type TaskStage, type TaskView } from '@/lib/tasks-board'
import { cn } from '@/utils/cn'
import { TaskAlertBadge, TaskExtraBadges, TaskPriorityBadge } from './TaskBadges'

interface TaskStageBoardProps {
  tasks: TaskView[]
  onOpen: (task: TaskView) => void
  /** Arrastou para outra coluna (Aguardando pede os dados na gaveta). */
  onMove: (task: TaskView, stage: TaskStage) => void
}

function Card({ task, onOpen }: { task: TaskView; onOpen: (t: TaskView) => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: String(task.id), data: { task } })
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined
  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn('cursor-grab rounded-md border bg-card p-2 text-sm shadow-xs', isDragging && 'opacity-60')}
    >
      <button type="button" className="block w-full text-left font-medium hover:underline" onClick={() => onOpen(task)}>
        {task.title}
      </button>
      <p className="truncate text-xs text-muted-foreground">
        {task.assigned_user_name}{task.case_number ? ` · ${task.case_number}` : ''}
      </p>
      <div className="mt-1 flex flex-wrap items-center gap-1">
        <TaskPriorityBadge task={task} />
        <TaskAlertBadge task={task} />
        <TaskExtraBadges task={task} />
        <span className="text-xs tabular-nums text-muted-foreground">{formatDay(task.data_referencia)}</span>
      </div>
    </li>
  )
}

function Column({ stage, label, tasks, onOpen }: { stage: TaskStage; label: string; tasks: TaskView[]; onOpen: (t: TaskView) => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage })
  return (
    <section
      ref={setNodeRef}
      aria-label={label}
      className={cn('flex min-h-40 flex-col gap-2 rounded-lg bg-muted/40 p-2', isOver && 'ring-2 ring-primary/40')}
    >
      <h2 className="flex items-center justify-between px-1 text-sm font-semibold">
        {label}
        <span className="tabular-nums text-muted-foreground">{tasks.length}</span>
      </h2>
      <ul className="flex flex-col gap-2">
        {tasks.map((task) => <Card key={task.id} task={task} onOpen={onOpen} />)}
      </ul>
    </section>
  )
}

/** Quadro por etapa: arrastar o cartão muda a etapa (servidor decide revisão/concluir). */
export function TaskStageBoard({ tasks, onOpen, onMove }: TaskStageBoardProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  )

  const onDragEnd = (event: DragEndEvent) => {
    const task = event.active.data.current?.task as TaskView | undefined
    const stage = event.over?.id as TaskStage | undefined
    if (task && stage && stage !== task.etapa) onMove(task, stage)
  }

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {BOARD_STAGES.map((s) => (
          <Column key={s.value} stage={s.value} label={s.label} tasks={tasks.filter((t) => t.etapa === s.value)} onOpen={onOpen} />
        ))}
      </div>
    </DndContext>
  )
}
